import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { gradeApi } from '../../api.js';
import { useAuth } from '../../auth/AuthContext';
import SideBar from '../../components/SideBar';
import BigStepper from '../../components/Stepper.js';
import BigStepperLine from '../../components/StepperLine.js';
import ExamUpload from '../../components/Teacher/TGrading/ExamUpload';
import RenderBatch from '../../components/Teacher/TGrading/RenderBatch';
import TGrade from '../../components/Teacher/TGrading/TGrade';
import ParsePDF from '../../components/Teacher/TParsePDF';
import TPset from '../../components/Teacher/TPset/TPset';
import TQuestionGrade from '../../components/Teacher/TGrading/TQuestionGrade.js';
import TopBar from '../../components/TopBar';
import AnswerUpload from '../../components/Teacher/TGrading/AnswerUpload';
import TAssignmentStats from '../../components/Teacher/Statistics/TAssignmentStats';
import ReleaseMethodSelection from '../../components/Teacher/TGrading/ReleaseMethodSelection';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './submissions.scss';

const TPdfSubmissions = ({
  assignment_id,
  assignment,
  submissions,
  pullQuestions,
  updateTrigger,
  section,
  reviewMode,
  setUpdateTrigger,
  setPullQuestions,
  setEdited,
  edited,
  solRecent,
  setSection,
  setSolRecent,
  isSubmissionLoading,
  pageNumber,
  itemsPerPage,
  submissionsError,
  setReviewMode,
  updateSearchParams,
  search,
  sortOrder
}) => {
  const { user } = useAuth();

  const [generated, setGenerated] = useState(false);
  const [topErrorMessages, setTopErrorMessages] = useState('');

  const [isReleaseMode, setIsReleaseMode] = useState(false);
  const [releaseOptions, setReleaseOptions] = useState({
    submissionPdf: true,
    grades: true,
    feedback: true
  });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [hasReleasedGrades, setHasReleasedGrades] = useState(false);
  const [isReleasingGrades, setIsReleasingGrades] = useState(false);
  const [showGradeOptionsDropdown, setShowGradeOptionsDropdown] = useState(false);

  // Release method selection state
  const [showReleaseMethodSelection, setShowReleaseMethodSelection] = useState(false);
  const [releaseAnalysis, setReleaseAnalysis] = useState(null);
  const [selectedReleaseMethod, setSelectedReleaseMethod] = useState('email');
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

  const navigate = useNavigate();

  // Check if any students have grades released
  useEffect(() => {
    if (submissions?.entities) {
      const anyReleased = submissions.entities.some(s => s.is_grade_released);
      setHasReleasedGrades(anyReleased);
    }
  }, [submissions]);

  const handleReleaseGradesClick = () => {
    // Filter out eligible student IDs (not unmatched, have submitted, and haven't received grade emails yet)
    const selectableStudentIds = (submissions?.entities ?? [])
      .filter(
        s =>
          s.first_name !== 'Unmatched Submission' &&
          s.status !== 'Not submitted' &&
          !(s.settings && s.settings.grade_released_at) // Don't auto-select students who already received emails
      )
      .map(s => s.id);

    // Set release mode and auto-select eligible students
    setIsReleaseMode(true);
    setSelectedStudents(selectableStudentIds);
  };

  const handleCancelRelease = () => {
    setIsReleaseMode(false);
    setSelectedStudents([]);
    setShowReleaseMethodSelection(false);
    setReleaseAnalysis(null);
    setSelectedReleaseMethod('email');
  };

  // Analyze Google Classroom submissions for release method recommendation
  const analyzeReleaseOptions = async () => {
    if (!assignment?.id) {
      return;
    }

    setIsLoadingAnalysis(true);
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment.id}/analyze-release-options/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      setReleaseAnalysis(response.data);
      setSelectedReleaseMethod(response.data.recommended_method);
      return response.data;
    } catch (error) {
      console.error('Error analyzing release options:', error);
      toast.error('Failed to analyze release options');
      return null;
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // Handle proceeding to release method selection
  const handleProceedToReleaseMethod = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student to release grades.');
      return;
    }

    const analysis = await analyzeReleaseOptions();
    if (analysis) {
      setShowReleaseMethodSelection(true);
    }
  };

  // Handle going back to student selection
  const handleBackToStudentSelection = () => {
    setShowReleaseMethodSelection(false);
    setReleaseAnalysis(null);
  };

  // Fetch release settings when component mounts or assignment changes
  useEffect(() => {
    if (assignment?.id) {
      fetchReleaseSettings(assignment.id);
    }
  }, [assignment?.id]);

  // Fetch release settings from the backend
  const fetchReleaseSettings = async assignmentId => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignmentId}/release-settings/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      if (response?.data?.settings) {
        // Map backend settings names to frontend names
        setReleaseOptions({
          submissionPdf: response.data.settings.show_pdf,
          grades: response.data.settings.show_grades,
          feedback: response.data.settings.show_feedback
        });
      }
    } catch (error) {
      console.error('Error fetching release settings:', error);
    }
  };

  // Handle release option change
  const handleReleaseOptionChange = async e => {
    const { name, checked } = e.target;

    // Update local state
    const updatedOptions = {
      ...releaseOptions,
      [name]: checked
    };
    setReleaseOptions(updatedOptions);

    // Send updated settings to backend
    try {
      // Map frontend settings names to backend names
      const settings = {
        show_pdf: updatedOptions.submissionPdf,
        show_grades: updatedOptions.grades,
        show_feedback: updatedOptions.feedback
      };

      await gradeApi.post({
        path: `/api/assignment/${assignment.id}/release-settings/`,
        body: { settings },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      toast.success('Grade release settings saved successfully');
      console.log('Release settings updated successfully');
    } catch (error) {
      console.error('Error updating release settings:', error);
      toast.error('Failed to save grade release settings');
    }
  };

  const handleStudentSelectionChange = (studentId, isSelected) => {
    // Check if studentId is actually an array (coming from Select All)
    if (Array.isArray(studentId)) {
      if (isSelected) {
        // Replace the entire selection with the passed array
        setSelectedStudents(studentId);
      } else {
        // Clear the selection (deselect all)
        setSelectedStudents([]);
      }
    } else {
      // Handle single student selection/deselection
      setSelectedStudents(prevSelected => {
        if (isSelected) {
          // Add the single ID if not already present
          return prevSelected.includes(studentId) ? prevSelected : [...prevSelected, studentId];
        } else {
          // Remove the single ID
          return prevSelected.filter(id => id !== studentId);
        }
      });
    }
  };

  const handleReleaseButtonClick = async () => {
    const studentIds = selectedStudents.map(id => Number(id));

    if (studentIds.length === 0) {
      toast.error('Please select at least one student to release grades.');
      return;
    }

    const loadingToastId = toast.loading('Releasing grades...');
    setIsReleasingGrades(true);

    try {
      let response;

      if (selectedReleaseMethod === 'email') {
        // Use existing email endpoint
        response = await gradeApi.post({
          path: `/api/assignment/${assignment.id}/email-grades/`,
          body: {
            student_ids: studentIds
          },
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
      } else {
        // Use Google Classroom release endpoint
        response = await gradeApi.post({
          path: `/api/assignment/${assignment.id}/release-grades-google-classroom/`,
          body: {
            student_ids: studentIds,
            release_method: selectedReleaseMethod
          },
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
      }

      console.log('Grades released successfully:', response.data);

      let successMessage = 'Grades released successfully!';
      if (selectedReleaseMethod === 'email') {
        successMessage = 'Grades released successfully! Emails will be sent shortly.';
      } else if (selectedReleaseMethod === 'google_classroom') {
        successMessage = 'Grades released to Google Classroom successfully!';
      } else if (selectedReleaseMethod === 'google_classroom_plus_email') {
        successMessage = 'Grades released to Google Classroom and email successfully!';
      }

      toast.update(loadingToastId, {
        render: successMessage,
        type: 'success',
        isLoading: false,
        autoClose: 5000
      });

      setSelectedStudents([]);
      setIsReleaseMode(false);
      setShowReleaseMethodSelection(false);
      setReleaseAnalysis(null);
      setSelectedReleaseMethod('email');
      setHasReleasedGrades(true);
      setUpdateTrigger(prev => !prev);
    } catch (error) {
      console.error('Error releasing grades:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'An error occurred while releasing grades.';
      toast.update(loadingToastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setIsReleasingGrades(false);
    }
  };

  // Handle sending grades to teacher (yourself)
  const handleSendGradesToSelf = async () => {
    const loadingToastId = toast.loading('Sending grades to yourself...');
    setIsReleasingGrades(true);

    try {
      const response = await gradeApi.post({
        path: `/api/assignment/${assignment.id}/email-grades/self/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      console.log('Grades sent to self successfully:', response.data);
      toast.update(loadingToastId, {
        render: 'Grades sent to yourself successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 5000
      });

      setShowGradeOptionsDropdown(false);
    } catch (error) {
      console.error('Error sending grades to self:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'An error occurred while sending grades to yourself.';
      toast.update(loadingToastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setIsReleasingGrades(false);
    }
  };

  const publishAssignment = async () => {
    if (assignment.is_assignment_published) {
      toast.error("You can't unpublish the assignment once it is published.");
      return;
    }

    if (!generated) {
      toast.error('Please generate the assignment PDF before publishing the assignment.');
      return;
    }
    if (edited) {
      toast.error(
        'You have made changes to the assignment without regenerating your PDF. Please regenerate PDF.'
      );
      return;
    }

    await gradeApi.get({
      path: `/api/assignment/${assignment.id}/build/`,
      config: { headers: { Authorization: `Bearer ${user.session_token}` } }
    });

    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment.id}/publish-assignment/`,
        body: {
          is_assignment_published: true
        },
        headers: {
          Authorization: `Bearer ${user.session_token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Assignment published successfully!');
      setUpdateTrigger(prev => !prev);
      window.location.reload();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to publish assignment. Please try again.');
      }
      console.error('Error publishing assignment:', error);
    }
  };

  useEffect(() => {
    if (assignment?.type === 'byob_pdf_problem_set' && assignment?.is_assignment_published) {
      setSection('review');
    }
  }, [assignment, setSection]);

  // Add useRef for dropdown menu
  const dropdownRef = React.useRef(null);

  // Handle clicking outside of dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGradeOptionsDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div id="TPdfSubmissions" className="relative min-h-screen bg-white flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="flex flex-row h-full">
        <div
          id="Left-Sidebar"
          className="sticky top-0 h-screen overflow-y-auto w-side-bar-size flex-shrink-0"
        >
          <SideBar activePage="Home" />
        </div>
        {/* Main content column to the right of the sidebar */}
        <div className="flex-grow h-screen overflow-hidden flex flex-col bg-white">
          {/* TopBar is now a direct child, spanning full width of this column */}
          <TopBar
            assignment_in={assignment}
            submissions_in={submissions}
            topErrorMessages={topErrorMessages}
            setTopErrorMessages={setTopErrorMessages}
            section={section}
            setSection={setSection}
            publishAssignment={publishAssignment}
            isReleaseMode={isReleaseMode}
            handleReleaseGradesClick={handleReleaseGradesClick}
            className="w-full flex-shrink-0"
          />
          {/* Scrollable content area below TopBar */}
          <div className="flex-grow overflow-y-auto pr-[30px] w-full">
            {/* Main content area - conditionally render based on isReleaseMode */}
            {!isReleaseMode && (
              <div className={`transition-opacity duration-300 ease-in-out opacity-100`}>
                {section === 'create' && assignment.type !== 'byob_pdf_problem_set' ? (
                  <div
                    className={`flex flex-col mb-32 w-[55%] ml-[15px] mr-0 ${topErrorMessages ? 'mt-16' : 'mt-20'}`}
                  >
                    <div className="flex gap-3 mb-6">
                      <button
                        className="group flex items-center gap-2 px-4 py-2 bg-white rounded-full
                                                        text-sm text-gray-700 border border-gray-200 hover:border-orange-200
                                                        hover:bg-orange-50 transition-all duration-200"
                        onClick={() => window.open('https://youtu.be/kPP4Rbn2YCE', '_blank')}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="w-4 h-4 text-gray-500 group-hover:text-orange-500 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M10 9v6l5-3-5-3z" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        Tutorial
                      </button>
                      <Link
                        to="/support"
                        className="group flex items-center gap-2 px-4 py-2 bg-white rounded-full
                                                        text-sm text-gray-700 border border-gray-200 hover:border-orange-200
                                                        hover:bg-orange-50 transition-all duration-200"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="w-4 h-4 text-gray-500 group-hover:text-orange-500 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                          <path d="M12 17h.01" />
                        </svg>
                        Help
                      </Link>
                    </div>

                    <div className="grid grid-cols-[84px_auto]">
                      <BigStepper stepNumber={0} top={5} />
                      <ParsePDF
                        assignment_id={assignment_id}
                        setUpdateTrigger={setUpdateTrigger}
                        isPublished={assignment.is_assignment_published}
                        setPullQuestions={setPullQuestions}
                        setEdited={setEdited}
                        setSolRecent={setSolRecent}
                      />
                    </div>
                    <BigStepperLine />

                    <div className="grid grid-cols-[84px_auto]">
                      <BigStepper stepNumber={1} top={5} />
                      <TPset
                        assignment_id={assignment_id}
                        updateTrigger={updateTrigger}
                        setUpdateTrigger={setUpdateTrigger}
                        pullQuestions={pullQuestions}
                        setPullQuestions={setPullQuestions}
                        isPublished={assignment.is_assignment_published}
                        setEdited={setEdited}
                        edited={edited}
                        setSolRecent={setSolRecent}
                        solRecent={solRecent}
                        isExam={assignment.is_exam}
                        setGenerated={setGenerated}
                        setTopErrorMessages={setTopErrorMessages}
                      />
                    </div>
                    <BigStepperLine />
                    <div className="grid grid-cols-[84px_auto] gap-4">
                      <BigStepper stepNumber={2} top={0} />
                      <AnswerUpload
                        assignment_id={assignment_id}
                        setUpdateTrigger={setUpdateTrigger}
                        isPublished={assignment.is_assignment_published}
                        setPullQuestions={setPullQuestions}
                        setEdited={setEdited}
                        setSolRecent={setSolRecent}
                      />
                    </div>
                  </div>
                ) : section === 'create' && assignment.type === 'byob_pdf_problem_set' ? (
                  <div
                    className={`flex flex-col mb-32 w-full ${topErrorMessages ? 'mt-16' : 'mt-20'}`}
                  >
                    <div className="flex flex-col w-full">
                      <div className="grid grid-cols-[84px_auto]">
                        <BigStepper stepNumber={0} top={5} />
                        <ParsePDF
                          assignment_id={assignment_id}
                          setUpdateTrigger={setUpdateTrigger}
                          isPublished={assignment.is_assignment_published}
                          setPullQuestions={setPullQuestions}
                          setEdited={setEdited}
                          setSolRecent={setSolRecent}
                        />
                      </div>
                      <BigStepperLine />

                      <div className="grid grid-cols-[84px_auto]">
                        <BigStepper stepNumber={1} top={5} />
                        <TPset
                          assignment_id={assignment_id}
                          updateTrigger={updateTrigger}
                          setUpdateTrigger={setUpdateTrigger}
                          pullQuestions={pullQuestions}
                          setPullQuestions={setPullQuestions}
                          isPublished={assignment.is_assignment_published}
                          setEdited={setEdited}
                          edited={edited}
                          setSolRecent={setSolRecent}
                          solRecent={solRecent}
                          isExam={assignment.is_exam}
                          setGenerated={setGenerated}
                          setTopErrorMessages={setTopErrorMessages}
                        />
                      </div>
                      <BigStepperLine />
                      <div className="grid grid-cols-[84px_auto] gap-4">
                        <BigStepper stepNumber={2} top={0} />
                        <AnswerUpload
                          assignment_id={assignment_id}
                          setUpdateTrigger={setUpdateTrigger}
                          isPublished={assignment.is_assignment_published}
                          setPullQuestions={setPullQuestions}
                          setEdited={setEdited}
                          setSolRecent={setSolRecent}
                        />
                      </div>
                    </div>
                  </div>
                ) : section === 'stats' ? (
                  <div
                    className={`flex flex-col mb-32 w-full ${topErrorMessages ? 'mt-16' : 'mt-20'}`}
                  >
                    {/*<div className="grid grid-cols-[84px_auto]">*/}
                    {/*    <BigStepper stepNumber={3} top={0} ht={'[50px]'}/>*/}
                    <TAssignmentStats
                      class_id={assignment.course_id}
                      assignment_id={assignment_id}
                    />
                    {/*</div>*/}
                  </div>
                ) : (
                  <div
                    className={`flex flex-col mb-32 w-full ${topErrorMessages ? 'mt-16' : 'mt-20'}`}
                  >
                    {assignment.is_exam && (
                      <>
                        <BigStepperLine />
                        <div className="grid grid-cols-[84px_auto]">
                          <BigStepper stepNumber={3} top={0} />
                          <ExamUpload
                            assignment_id={assignment_id}
                            setUpdateTrigger={setUpdateTrigger}
                          />
                        </div>
                        <BigStepperLine />
                      </>
                    )}

                    <div className="grid grid-cols-[84px_auto]">
                      <BigStepper stepNumber={assignment.is_exam ? 4 : 3} top={0} ht={'[50px]'} />
                      {reviewMode === 'Question' ? (
                        <TQuestionGrade
                          assignment_in={assignment}
                          submissions_in={submissions}
                          setUpdateTrigger={setUpdateTrigger}
                          reviewMode={reviewMode}
                          setReviewMode={setReviewMode}
                        />
                      ) : (
                        <TGrade
                          sortOrder={sortOrder}
                          search={search}
                          isLoading={isSubmissionLoading}
                          itemsPerPage={itemsPerPage}
                          pageNumber={pageNumber}
                          submissionsError={submissionsError}
                          assignment={assignment}
                          submissions={submissions}
                          setUpdateTrigger={setUpdateTrigger}
                          isReleaseMode={isReleaseMode}
                          selectedStudents={selectedStudents}
                          handleStudentSelectionChange={handleStudentSelectionChange}
                          reviewMode={reviewMode}
                          setReviewMode={setReviewMode}
                          updateSearchParams={updateSearchParams}
                          releaseOptions={releaseOptions}
                          handleReleaseOptionChange={handleReleaseOptionChange}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Release Grades Interface - shown when isReleaseMode is true */}
            {isReleaseMode && (
              <div className={`transition-opacity duration-300 ease-in-out opacity-100`}>
                <div
                  className={`flex flex-col mb-32 w-full ${topErrorMessages ? 'mt-16' : 'mt-20'}`}
                >
                  <TGrade
                    sortOrder={sortOrder}
                    search={search}
                    isLoading={isSubmissionLoading}
                    itemsPerPage={itemsPerPage}
                    pageNumber={pageNumber}
                    submissionsError={submissionsError}
                    assignment={assignment}
                    submissions={submissions}
                    setUpdateTrigger={setUpdateTrigger}
                    isReleaseMode={isReleaseMode} // This is true
                    selectedStudents={selectedStudents}
                    handleStudentSelectionChange={handleStudentSelectionChange}
                    reviewMode={reviewMode} // This should be 'Submissions' or similar for the table
                    setReviewMode={setReviewMode}
                    updateSearchParams={updateSearchParams}
                    releaseOptions={releaseOptions}
                    handleReleaseOptionChange={handleReleaseOptionChange}
                    // Pass relevant props for the release interface that TGrade renders
                    handleCancelRelease={handleCancelRelease} // Pass cancel handler
                    handleReleaseButtonClick={handleReleaseButtonClick} // Pass release handler
                    isReleasingGrades={isReleasingGrades} // Pass loading state for button
                    hasReleasedGrades={hasReleasedGrades} // To show relevant messages or UI states
                  />
                </div>
              </div>
            )}
            <div
              className={`fixed top-24 right-5 p-6 bg-white border border-gray-200 rounded-lg shadow-xl z-40 w-82 transition-all duration-300 ease-in-out ${
                isReleaseMode
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 pointer-events-none'
              }`}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Select students</h3>

              <div className="flex justify-end space-x-4">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all"
                  onClick={handleCancelRelease}
                >
                  Cancel
                </button>
                <div ref={dropdownRef} className="relative">
                  <div className="flex">
                    <button
                      id="release-button-panel"
                      className="px-5 py-2 min-w-[130px] bg-gradient-to-r from-[#e4b17a] to-[#c99b69] text-white rounded-l-md text-sm font-medium transition-all hover:shadow-md hover:-translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-y-0"
                      disabled={
                        selectedStudents.length === 0 || isReleasingGrades || isLoadingAnalysis
                      }
                      onClick={handleProceedToReleaseMethod}
                    >
                      {isLoadingAnalysis ? 'Analyzing...' : `Continue (${selectedStudents.length})`}
                    </button>
                    <button
                      className="flex items-center justify-center w-8 bg-gradient-to-r from-[#c99b69] to-[#c99b69] text-white rounded-r-md border-l border-[#b88c5c] hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
                      disabled={isReleasingGrades}
                      aria-label="Show send options"
                      onClick={() => setShowGradeOptionsDropdown(!showGradeOptionsDropdown)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Dropdown Menu */}
                  {showGradeOptionsDropdown && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                      <div className="py-1">
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                          onClick={handleSendGradesToSelf}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-2 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                            />
                          </svg>
                          Send to myself
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Release Method Selection Modal */}
      {showReleaseMethodSelection && (
        <ReleaseMethodSelection
          releaseAnalysis={releaseAnalysis}
          selectedReleaseMethod={selectedReleaseMethod}
          setSelectedReleaseMethod={setSelectedReleaseMethod}
          selectedStudents={selectedStudents}
          isLoading={isReleasingGrades}
          onBack={handleBackToStudentSelection}
          onContinue={handleReleaseButtonClick}
        />
      )}
    </div>
  );
};

export default TPdfSubmissions;
